import { setContext } from '@sentry/react';
import { RENDERING_CONTEXT_ATTRIBUTES } from '~/engine/core/render/renderingContextAttributes';

/**
 * A capability the viewer depends on that this browser does not provide.
 *
 * `blocking` means the viewer cannot display images correctly at all, so the
 * application refuses to start; `degraded` means it runs with a named
 * limitation the user should know about.
 */
export type SupportSeverity = 'blocking' | 'degraded';

export type BrowserSupportIssueCode =
    | 'unsupportedBrowser'
    | 'noOffscreenCanvas'
    | 'noWebGl2'
    | 'noStencilBuffer'
    | 'noImageBitmapRenderer'
    | 'noCreateImageBitmap'
    | 'noWebAssembly'
    | 'softwareRenderer'
    | 'noFloatColorBuffer'
    | 'noFloatLinearFiltering'
    | 'smallTextureLimits'
    | 'probeFailed';

export interface BrowserSupportIssue {
    readonly code: BrowserSupportIssueCode;
    readonly severity: SupportSeverity;
    /**
     * Technical, untranslated elaboration for support — a driver string, a
     * WebGL status message, a limit. Never PHI: everything reported here
     * describes the browser and the GPU, not the data loaded into them.
     */
    readonly detail?: string;
}

export interface BrowserSupportReport {
    readonly issues: readonly BrowserSupportIssue[];
    /** True when at least one issue is `blocking`. */
    readonly blocked: boolean;
    /**
     * PHI-free description of the browser and graphics stack, as
     * `key: value` lines, for the technical-details panel and for support
     * reports.
     */
    readonly diagnostics: string;
}

/**
 * Smallest 3D texture edge that holds a routine CT/MR volume in one texture.
 * The WebGL 2 floor is 256, which a full 512² transversal series already exceeds.
 */
const REQUIRED_3D_TEXTURE_SIZE = 512;

/**
 * Smallest 2D texture edge the viewport framebuffers and contour textures
 * are sized against.
 */
const REQUIRED_TEXTURE_SIZE = 4096;

/**
 * Renderer names of CPU rasterisers. These do produce correct pixels, but at
 * a frame rate that makes reviewing a volume slice by slice impractical.
 */
const CPU_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render/i;

function isFirefox(userAgent: string): boolean {
    return /firefox/i.test(userAgent) && !/seamonkey/i.test(userAgent);
}

/**
 * The renderer string, unmasked where the browser exposes it.
 *
 * `WEBGL_debug_renderer_info` is absent or restricted in several browsers;
 * `RENDERER` is always readable but may be a generic placeholder, which is
 * still worth recording.
 */
function rendererName(gl: WebGL2RenderingContext): string {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const unmasked = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : null;
    return String(unmasked ?? gl.getParameter(gl.RENDERER) ?? '');
}

function vendorName(gl: WebGL2RenderingContext): string {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const unmasked = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : null;
    return String(unmasked ?? gl.getParameter(gl.VENDOR) ?? '');
}

/**
 * Why the browser refused a WebGL 2 context, as reported by the
 * `webglcontextcreationerror` event. Chromium's status messages name the
 * actual cause ("GPU access is disabled", a failed command decoder), which
 * is the difference between an actionable report and "it didn't work".
 */
function creationErrorMessage(event: Event): string {
    return 'statusMessage' in event
        ? String((event as WebGLContextEvent).statusMessage)
        : '';
}

/**
 * Inspects a live context for capabilities that shape, rather than prevent,
 * clinical use, appending what it finds to `issues` and `diagnostics`.
 */
function inspectContext(
    gl: WebGL2RenderingContext,
    issues: BrowserSupportIssue[],
    diagnostics: string[]
): void {
    // getContextAttributes() returns null (rather than throwing) for a lost
    // context, and nothing below can be trusted on one.
    const attributes = gl.getContextAttributes();
    if (!attributes) {
        issues.push({
            code: 'noWebGl2',
            severity: 'blocking',
            detail: 'context attributes unreadable (context lost during probe)',
        });
        return;
    }

    const renderer = rendererName(gl);
    const max3dTextureSize = Number(gl.getParameter(gl.MAX_3D_TEXTURE_SIZE));
    const maxTextureSize = Number(gl.getParameter(gl.MAX_TEXTURE_SIZE));

    diagnostics.push(
        `webglVendor: ${vendorName(gl)}`,
        `webglRenderer: ${renderer}`,
        `webglVersion: ${String(gl.getParameter(gl.VERSION))}`,
        `stencil: ${String(attributes.stencil)}`,
        `maxTextureSize: ${maxTextureSize}`,
        `max3dTextureSize: ${max3dTextureSize}`
    );

    // A context granted without the stencil buffer that was asked for draws
    // clipped contours wrong rather than not at all, so it is refused here
    // instead of being discovered on screen.
    if (!attributes.stencil) {
        issues.push({
            code: 'noStencilBuffer',
            severity: 'blocking',
            detail: 'WebGL 2 context created without the requested stencil buffer',
        });
    }

    if (CPU_RENDERER.test(renderer)) {
        issues.push({
            code: 'softwareRenderer',
            severity: 'degraded',
            detail: renderer,
        });
    }

    if (gl.getExtension('EXT_color_buffer_float') === null) {
        issues.push({ code: 'noFloatColorBuffer', severity: 'degraded' });
    }

    if (gl.getExtension('OES_texture_float_linear') === null) {
        issues.push({ code: 'noFloatLinearFiltering', severity: 'degraded' });
    }

    if (
        max3dTextureSize < REQUIRED_3D_TEXTURE_SIZE ||
        maxTextureSize < REQUIRED_TEXTURE_SIZE
    ) {
        issues.push({
            code: 'smallTextureLimits',
            severity: 'degraded',
            detail:
                `MAX_3D_TEXTURE_SIZE ${max3dTextureSize} ` +
                `(need ${REQUIRED_3D_TEXTURE_SIZE}), ` +
                `MAX_TEXTURE_SIZE ${maxTextureSize} ` +
                `(need ${REQUIRED_TEXTURE_SIZE})`,
        });
    }
}

/**
 * Creates, inspects and releases a throwaway rendering context.
 *
 * The context is created exactly as the viewer creates its own — same canvas
 * kind, same {@link RENDERING_CONTEXT_ATTRIBUTES} — so a browser that fails
 * here is a browser the viewer cannot start in. It is released again through
 * `WEBGL_lose_context`: contexts are a scarce per-page resource, and a probe
 * that kept one could cost the viewer the context it is probing for.
 */
function probeRenderingContext(
    issues: BrowserSupportIssue[],
    diagnostics: string[]
): void {
    if (typeof OffscreenCanvas === 'undefined') {
        issues.push({
            code: 'noOffscreenCanvas',
            severity: 'blocking',
            detail: 'OffscreenCanvas is not implemented',
        });
        return;
    }

    let canvas: OffscreenCanvas;
    try {
        canvas = new OffscreenCanvas(1, 1);
    } catch (error) {
        issues.push({
            code: 'noOffscreenCanvas',
            severity: 'blocking',
            detail: String(error),
        });
        return;
    }

    let creationError = '';
    const onCreationError = (event: Event) => {
        creationError = creationErrorMessage(event);
    };

    let gl: WebGL2RenderingContext | null = null;
    canvas.addEventListener('webglcontextcreationerror', onCreationError);
    try {
        gl = canvas.getContext(
            'webgl2',
            RENDERING_CONTEXT_ATTRIBUTES
        ) as WebGL2RenderingContext | null;
    } catch (error) {
        creationError = creationError || String(error);
    } finally {
        canvas.removeEventListener(
            'webglcontextcreationerror',
            onCreationError
        );
    }

    if (!gl) {
        issues.push({
            code: 'noWebGl2',
            severity: 'blocking',
            detail: creationError || 'getContext("webgl2") returned null',
        });
        return;
    }

    try {
        inspectContext(gl, issues, diagnostics);
    } catch (error) {
        // A context that cannot even be interrogated is not one to start the
        // viewer on: the checks below it never ran, so nothing is known
        // about the capabilities they cover.
        issues.push({
            code: 'noWebGl2',
            severity: 'blocking',
            detail: `context inspection failed: ${String(error)}`,
        });
    } finally {
        gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
}

/**
 * Whether a rendered frame can be handed to a visible canvas.
 *
 * The viewer composites offscreen and presents through a `bitmaprenderer`
 * context. Where that context is unavailable the presentation step silently
 * does nothing — panes stay blank with no error raised — so its absence is
 * treated as blocking.
 */
function probeFramePresentation(issues: BrowserSupportIssue[]): void {
    if (typeof createImageBitmap !== 'function') {
        issues.push({
            code: 'noCreateImageBitmap',
            severity: 'blocking',
            detail: 'createImageBitmap is not implemented',
        });
    }

    const canvas = document.createElement('canvas');
    let bitmapRenderer: ImageBitmapRenderingContext | null = null;
    try {
        bitmapRenderer = canvas.getContext('bitmaprenderer');
    } catch {
        bitmapRenderer = null;
    }
    if (!bitmapRenderer) {
        issues.push({
            code: 'noImageBitmapRenderer',
            severity: 'blocking',
            detail: 'getContext("bitmaprenderer") returned null',
        });
    }
}

/**
 * Establishes whether this browser can run the viewer, and what it gives up
 * if it can.
 *
 * Every check probes the capability itself rather than inferring it from the
 * user agent, so a browser is blocked only for something the viewer
 * genuinely needs and cannot obtain. The one exception is Firefox, which is
 * excluded by product policy rather than by any missing capability.
 *
 * The returned report carries no patient data: it describes the browser and
 * the graphics stack only.
 */
export function checkBrowserSupport(): BrowserSupportReport {
    const issues: BrowserSupportIssue[] = [];
    const diagnostics: string[] = [];

    try {
        const userAgent = navigator.userAgent;
        diagnostics.push(`userAgent: ${userAgent}`);

        if (isFirefox(userAgent)) {
            issues.push({ code: 'unsupportedBrowser', severity: 'blocking' });
        }

        if (typeof WebAssembly === 'undefined') {
            issues.push({
                code: 'noWebAssembly',
                severity: 'blocking',
                detail: 'WebAssembly is not implemented',
            });
        }

        probeFramePresentation(issues);
        probeRenderingContext(issues, diagnostics);
    } catch (error) {
        // A probe that could not run to completion leaves the capabilities
        // it covers unknown, which is a blocking verdict, not a pass.
        issues.push({
            code: 'probeFailed',
            severity: 'blocking',
            detail: String(error),
        });
    }

    for (const issue of issues) {
        diagnostics.push(
            `${issue.severity}: ${issue.code}` +
                (issue.detail ? ` (${issue.detail})` : '')
        );
    }

    return {
        issues,
        blocked: issues.some((issue) => issue.severity === 'blocking'),
        diagnostics: diagnostics.join('\n'),
    };
}

let report: BrowserSupportReport | undefined;

/**
 * The support report for this session, probed once on first use.
 *
 * Probing allocates and releases a rendering context, so callers that only
 * need the verdict — the startup gate, the degraded-capability warning —
 * share one result rather than repeating it.
 *
 * The first probe records what it found at debug level, and attaches it to
 * every Sentry event of the session as the `browser_support` context, so an
 * error report carries the graphics stack it happened on. The report is
 * PHI-free.
 */
export function getBrowserSupport(): BrowserSupportReport {
    if (!report) {
        report = checkBrowserSupport();
        console.debug(`Browser support\n${report.diagnostics}`);
        setContext('browser_support', {
            blocked: report.blocked,
            issues: report.issues.map((issue) => issue.code),
            diagnostics: report.diagnostics,
        });
    }
    return report;
}
